import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';
import api from '../utils/api';

const ChatContext = createContext(null);
export const useChatContext = () => useContext(ChatContext);

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'https://hrms.teleforce.cx';

export const ChatProvider = ({ children }) => {
  const { user } = useAuth();
  const [socket, setSocket] = useState(null);
  const [totalUnread, setTotalUnread] = useState(0);
  const activeRoomIdRef = useRef(null);

  // Request browser notification permission once
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Fetch initial unread count
  useEffect(() => {
    if (!user) return;
    api.get('/chat/rooms')
      .then(res => {
        const count = res.data.reduce((sum, r) => sum + (r.unreadCount || 0), 0);
        setTotalUnread(count);
      })
      .catch(() => {});
  }, [user]);

  // Connect socket when user logs in
  useEffect(() => {
    if (!user) { setSocket(null); return; }
    const token = localStorage.getItem('token');
    const s = io(SOCKET_URL, { auth: { token }, transports: ['websocket', 'polling'] });
    s.on('connect', () => s.emit('join_rooms'));
    s.on('connect_error', err => console.error('[Chat] socket error:', err.message));
    setSocket(s);
    return () => { s.disconnect(); setSocket(null); };
  }, [user]);

  // Global new_message — badge + notification
  useEffect(() => {
    if (!socket || !user) return;
    const onMessage = (msg) => {
      const senderId = msg.sender?._id;
      const myId = user._id || user.id;
      if (senderId === myId) return; // own message

      const isActiveRoom = activeRoomIdRef.current === msg.roomId;
      if (!isActiveRoom) {
        setTotalUnread(prev => prev + 1);

        const senderName = msg.sender?.name || 'Someone';
        const preview = msg.content.length > 60 ? msg.content.slice(0, 60) + '…' : msg.content;

        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(senderName, { body: preview, icon: '/logo192.png' });
        } else {
          toast.info(`💬 ${senderName}: ${preview}`, { autoClose: 4000 });
        }
      }
    };
    socket.on('new_message', onMessage);
    return () => socket.off('new_message', onMessage);
  }, [socket, user]);

  const setActiveRoomId = useCallback((id) => {
    activeRoomIdRef.current = id;
  }, []);

  const markRoomRead = useCallback((unreadCount) => {
    setTotalUnread(prev => Math.max(0, prev - unreadCount));
  }, []);

  return (
    <ChatContext.Provider value={{ socket, totalUnread, setActiveRoomId, markRoomRead }}>
      {children}
    </ChatContext.Provider>
  );
};
